// Constants
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyPlansPagePivotType, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
/**
 * Get the View key
 * @param viewName Name of the view
 * @param viewPartitionKey View partition key
 */ export function getViewKey(viewName, viewPartitionKey) {
    return `${viewName}_${viewPartitionKey}`;
}
/**
 * Immutably updates view settings for a specific view ID.
 * Safely handles nested object updates without mutation.
 *
 * @param currentViewSettings - The current view settings object
 * @param viewIdKey - The stringified view ID key
 * @param updates - Partial updates to merge into the view's settings
 * @returns A new view settings object with the updates applied
 *
 * @example
 * ```typescript
 * const updated = updateViewSettings(
 *   this.viewSettingsRoamingData,
 *   viewIdKey,
 *   { gridColumns: ["col1", "col2"] }
 * );
 * ```
 */ export function updateViewSettings(currentViewSettings, viewIdKey, updates) {
    return {
        ...currentViewSettings,
        [viewIdKey]: {
            ...currentViewSettings[viewIdKey],
            ...updates
        }
    };
}
/**
 * Convert a view id to a string representation
 * @param viewId The view id
 */ export function toViewIdString(viewId) {
    return `${viewId.name}_${viewId.partitionKey}`;
}
/**
 * Some views share filter and grouping scopes. This function returns all view ids that share the same filter and grouping scope as the provided view id.
 * While applying a filter/grouping on any of these views, the same filter/grouping should be applied to all other views in the same scope.
 * @param viewId The view id
 * @returns An array of view ids that share the same filter and grouping scope
 */ export function getViewIdsInSameFilterAndGroupingScope(viewId) {
    let viewIds = [];
    switch(viewId.name){
        case ViewName.MyTasksPage:
        case ViewName.MyTasksListPage:
            {
                viewIds = Object.values(MyTasksViewPivotType).map((pivot)=>({
                        name: viewId.name,
                        partitionKey: pivot
                    }));
                break;
            }
        case ViewName.MyPlansPage:
            viewIds = Object.values(MyPlansPagePivotType).map((pivot)=>({
                    name: ViewName.MyPlansPage,
                    partitionKey: pivot
                }));
            break;
        default:
            viewIds = [
                viewId
            ];
    }
    return viewIds;
}
/**
 * Determines whether the user has an explicitly stored grouping preference.
 *
 * Uses a two-call sentinel approach against the view store:
 * - `storeGrouping`:  result of `getCurrentTaskGroupingState(viewId)` — returns the stored value,
 *   or falls back to `getDefaultGroupingOption()` (e.g. Status for MyTasksPage/MyTasksList; never None).
 * - `userGrouping`:  result of `getCurrentTaskGroupingState(viewId, true)` — returns the stored value,
 *   or falls back to `TaskGroupingOption.None` as a sentinel.
 *
 * If `userGrouping` is None **and** `storeGrouping` is *not* None, no preference was stored
 * (the sentinel was returned).  Any other combination means the user explicitly chose a value.
 *
 * **INVARIANT:** `getDefaultGroupingOption(MyTasksPage/MyTasksList)` must never return `None`,
 * otherwise the sentinel becomes indistinguishable from a real default.
 *
 * @param storeGrouping  Value from `getCurrentTaskGroupingState(viewId)`
 * @param userGrouping   Value from `getCurrentTaskGroupingState(viewId, true)`
 * @returns `true` when the user has an explicitly stored grouping preference
 */ export function hasStoredGroupingPreference(storeGrouping, userGrouping) {
    // Invariant: getDefaultGroupingOption(MyTasksPage/MyTasksList) !== TaskGroupingOption.None
    // If the default ever becomes None, this sentinel logic cannot distinguish "no preference" from "user chose None".
    return userGrouping !== TaskGroupingOption.None || storeGrouping === TaskGroupingOption.None;
}
/**
 * Get the default grouping option for a view id
 * @param viewId The view id
 * @returns The default grouping option
 */ export function getDefaultGroupingOption(viewId) {
    switch(viewId.name){
        case ViewName.MyDayPage:
        case ViewName.MyTasksPage:
        case ViewName.MyTasksListPage:
            return TaskGroupingOption.Status;
        case ViewName.PlanPage:
            return TaskGroupingOption.Bucket;
        default:
            return TaskGroupingOption.None;
    }
}
